import type { BoardObjectKind, NoteColor, Board } from './types'

/**
 * Starting layouts for a new board.
 *
 * Templates live in code, not in the database: they're content that ships with
 * the app and should version with it, and a new board is created rarely enough
 * that the extra round trip to fetch them would buy nothing. Applying one is a
 * single bulk insert after the board row exists.
 *
 * Coordinates are board-space and absolute. The canvas opens at origin with no
 * pan, so everything is laid out in a ~1500x900 region starting near (60, 60) —
 * roughly one screen at 100% zoom, which means a freshly-created board shows the
 * whole template without anyone having to hunt for it.
 *
 * Dimensions must be non-negative. `board_objects` carries
 * `CHECK (width >= 0 AND height >= 0)`, so an arrow can't point left or up via a
 * negative vector — move its origin instead. Getting this wrong rejects the
 * whole insert, and the board creation is rolled back with it.
 *
 * Deliberately no connectors between shapes: a connected arrow stores the ids of
 * the two objects it joins, and those ids don't exist until after the insert.
 * Wiring them would need a second round trip to resolve them, for arrows the
 * team will redraw anyway. Free-floating arrows (which carry their own geometry)
 * are used where flow direction genuinely matters.
 */

export interface TemplateObject {
  kind: BoardObjectKind
  x: number
  y: number
  width: number
  height: number
  text?: string
  color?: NoteColor
  /** Paint order. Frames are drawn behind regardless, but z orders siblings. */
  z?: number
  font_size?: number
}

export interface BoardTemplate {
  id: string
  name: string
  /** One line, shown under the name in the picker. */
  description: string
  background: Board['background']
  /** Rough shape of the layout, for the picker's thumbnail. */
  preview: Array<{ x: number; y: number; w: number; h: number; color?: NoteColor; outline?: boolean; round?: boolean }>
  objects: TemplateObject[]
}

/** A titled frame plus its caption, the building block of most of these. */
function zone(
  x: number,
  y: number,
  width: number,
  height: number,
  title: string,
  opts: { color?: NoteColor; note?: string; z?: number } = {},
): TemplateObject[] {
  const out: TemplateObject[] = [{ kind: 'frame', x, y, width, height, text: title, color: opts.color ?? 'gray', z: 0 }]
  if (opts.note) {
    out.push({
      kind: 'text',
      x: x + 16,
      y: y + 14,
      width: width - 32,
      height: 28,
      text: opts.note,
      color: 'gray',
      font_size: 13,
      z: opts.z ?? 1,
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// 1. Architecture — the IDesign static architecture, empty and ready to fill.
//
// Layer names follow how DPL actually teaches and writes it (see Rali's
// docs/IDesignArchitecture.md): Managers orchestrate use cases, Engines hold
// computation, Accessors encapsulate resource access, Utilities cut across
// every layer. The sample components are named as prompts, not answers — the
// point of the exercise is deciding what the team's own Managers and Accessors
// should be, by volatility rather than by function.
// ---------------------------------------------------------------------------
const architecture: BoardTemplate = {
  id: 'architecture',
  name: 'Architecture (IDesign)',
  description: 'The IDesign layers — Clients, Managers, Engines, Accessors, Resources, Utilities.',
  background: 'grid',
  preview: [
    { x: 6, y: 4, w: 62, h: 12, outline: true },
    { x: 6, y: 20, w: 62, h: 20, color: 'green' },
    { x: 6, y: 44, w: 62, h: 16, color: 'blue' },
    { x: 6, y: 64, w: 62, h: 12, outline: true },
    { x: 72, y: 4, w: 22, h: 72, color: 'orange' },
  ],
  objects: [
    { kind: 'text', x: 60, y: 20, width: 700, height: 34, text: 'System Architecture — IDesign', color: 'gray', font_size: 22, z: 10 },
    {
      kind: 'text', x: 60, y: 52, width: 900, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'Decompose by volatility, not by function. Ask what changes independently — that is where a boundary belongs.',
    },

    // Clients — what drives the system.
    ...zone(60, 96, 1000, 130, 'Clients', { note: 'Who or what calls in? Web, mobile, scheduled jobs, other systems.' }),
    { kind: 'note', x: 100, y: 140, width: 180, height: 70, text: 'Web app', color: 'gray', z: 2 },
    { kind: 'note', x: 300, y: 140, width: 180, height: 70, text: 'Mobile', color: 'gray', z: 2 },
    { kind: 'note', x: 500, y: 140, width: 180, height: 70, text: 'Scheduled job', color: 'gray', z: 2 },

    // Business logic — Managers (use cases) and Engines (computation).
    ...zone(60, 250, 1000, 210, 'Business Logic', { note: 'Managers orchestrate use cases · Engines hold the rules and computation.' }),
    { kind: 'note', x: 100, y: 296, width: 190, height: 90, text: 'SomethingManager\n\none use case', color: 'green', z: 2 },
    { kind: 'note', x: 310, y: 296, width: 190, height: 90, text: 'AnotherManager', color: 'green', z: 2 },
    { kind: 'note', x: 620, y: 296, width: 190, height: 90, text: 'SomethingEngine\n\nrules / calculation', color: 'blue', z: 2 },
    { kind: 'text', x: 100, y: 398, width: 400, height: 24, text: 'Managers — orchestration, one per use case', color: 'gray', font_size: 12, z: 2 },
    { kind: 'text', x: 620, y: 398, width: 400, height: 24, text: 'Engines — computation, reusable across Managers', color: 'gray', font_size: 12, z: 2 },

    // Resource access — Accessors.
    ...zone(60, 484, 1000, 170, 'Resource Access', { note: 'Accessors encapsulate one resource each. Atomic business verbs, no use-case logic.' }),
    { kind: 'note', x: 100, y: 530, width: 190, height: 90, text: 'SomethingAccess', color: 'purple', z: 2 },
    { kind: 'note', x: 310, y: 530, width: 190, height: 90, text: 'AnotherAccess', color: 'purple', z: 2 },
    { kind: 'note', x: 520, y: 530, width: 190, height: 90, text: 'ExternalApiAccess', color: 'purple', z: 2 },

    // Resources.
    ...zone(60, 678, 1000, 150, 'Resources', { note: 'Databases, queues, third-party APIs — the things that actually store or do.' }),
    { kind: 'note', x: 100, y: 722, width: 190, height: 76, text: 'Database', color: 'gray', z: 2 },
    { kind: 'note', x: 310, y: 722, width: 190, height: 76, text: 'Message bus', color: 'gray', z: 2 },
    { kind: 'note', x: 520, y: 722, width: 190, height: 76, text: 'Third-party API', color: 'gray', z: 2 },

    // Utilities cut across every layer, so they sit alongside rather than within.
    ...zone(1090, 96, 300, 732, 'Utilities', { note: 'Cross-cutting: security, logging, diagnostics, config.' }),
    { kind: 'note', x: 1120, y: 150, width: 240, height: 66, text: 'Security', color: 'orange', z: 2 },
    { kind: 'note', x: 1120, y: 232, width: 240, height: 66, text: 'Logging', color: 'orange', z: 2 },
    { kind: 'note', x: 1120, y: 314, width: 240, height: 66, text: 'Diagnostics', color: 'orange', z: 2 },
    { kind: 'note', x: 1120, y: 396, width: 240, height: 66, text: 'Configuration', color: 'orange', z: 2 },

    // Call direction: calls go DOWN the layers, never up or sideways.
    { kind: 'arrow', x: 1005, y: 230, width: 0, height: 60, color: 'gray', z: 5 },
    { kind: 'arrow', x: 1005, y: 464, width: 0, height: 60, color: 'gray', z: 5 },
    { kind: 'arrow', x: 1005, y: 658, width: 0, height: 60, color: 'gray', z: 5 },
    { kind: 'text', x: 880, y: 860, width: 520, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'Calls go down the layers. Never sideways within a layer, never up.' },
  ],
}

// ---------------------------------------------------------------------------
// 2. Retro — three swim lanes.
// ---------------------------------------------------------------------------
const retro: BoardTemplate = {
  id: 'retro',
  name: 'Retro',
  description: 'Three lanes — what worked, what we learned, what to improve.',
  background: 'dots',
  preview: [
    { x: 5, y: 14, w: 28, h: 62, color: 'green' },
    { x: 36, y: 14, w: 28, h: 62, color: 'blue' },
    { x: 67, y: 14, w: 28, h: 62, color: 'orange' },
  ],
  objects: [
    { kind: 'text', x: 60, y: 24, width: 700, height: 34, text: 'Retro', color: 'gray', font_size: 22, z: 10 },
    { kind: 'text', x: 60, y: 56, width: 900, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'One note per thought. Write first, discuss after — otherwise the loudest voice sets the agenda.' },

    ...zone(60, 100, 420, 740, 'What worked', { note: 'Keep doing this.' }),
    { kind: 'note', x: 95, y: 150, width: 170, height: 110, text: '', color: 'green', z: 2 },
    { kind: 'note', x: 280, y: 150, width: 170, height: 110, text: '', color: 'green', z: 2 },

    ...zone(500, 100, 420, 740, 'What we learned', { note: 'New information, surprises, things we now know.' }),
    { kind: 'note', x: 535, y: 150, width: 170, height: 110, text: '', color: 'blue', z: 2 },
    { kind: 'note', x: 720, y: 150, width: 170, height: 110, text: '', color: 'blue', z: 2 },

    ...zone(940, 100, 420, 740, 'What to improve', { note: 'Each one needs an owner and a next step, or it will be here again next time.' }),
    { kind: 'note', x: 975, y: 150, width: 170, height: 110, text: '', color: 'orange', z: 2 },
    { kind: 'note', x: 1160, y: 150, width: 170, height: 110, text: '', color: 'orange', z: 2 },

    { kind: 'text', x: 940, y: 790, width: 400, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'Actions: owner + first step + when.' },
  ],
}

// ---------------------------------------------------------------------------
// 3. Value Proposition Canvas — Osterwalder's two halves.
//
// Customer profile on the right, value map on the left, because the canvas is
// meant to be read right-to-left: understand the customer first, then argue
// that what you build actually addresses them. The pairings are the point —
// pain relievers answer pains, gain creators answer gains.
// ---------------------------------------------------------------------------
const valueProp: BoardTemplate = {
  id: 'value-proposition',
  name: 'Value Proposition Canvas',
  description: 'The square value map facing the circular customer profile.',
  background: 'dots',
  preview: [
    // square, divided: products left, gain creators top-right, pain relievers below
    { x: 4, y: 16, w: 20, h: 58, color: 'blue' },
    { x: 25, y: 16, w: 20, h: 28, color: 'green' },
    { x: 25, y: 45, w: 20, h: 29, color: 'purple' },
    // circle
    { x: 52, y: 14, w: 44, h: 62, color: 'gray', round: true },
  ],
  objects: [
    { kind: 'text', x: 60, y: 20, width: 800, height: 34, text: 'Value Proposition Canvas', color: 'gray', font_size: 22, z: 10 },
    { kind: 'text', x: 60, y: 52, width: 1100, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'Fill the customer side first, then argue the value side against it. Fit is when the two halves line up.' },

    { kind: 'text', x: 60, y: 112, width: 400, height: 26, text: 'Value Map — what we offer', color: 'gray', font_size: 15, z: 10 },
    { kind: 'text', x: 840, y: 112, width: 460, height: 26, text: 'Customer Profile — what they need', color: 'gray', font_size: 15, z: 10 },

    // ---- the square, tiled into its three regions --------------------------
    // Three filled rects rather than one shape with lines drawn on it: the
    // canvas has no line primitive and no rotation, so tiling is how a divided
    // square gets drawn at all. The seams between the tiles ARE the divisions.
    { kind: 'rect', x: 60, y: 150, width: 340, height: 680, color: 'blue', z: 0, text: 'Products & Services', font_size: 15 },
    { kind: 'rect', x: 400, y: 150, width: 340, height: 340, color: 'green', z: 0, text: 'Gain Creators', font_size: 15 },
    { kind: 'rect', x: 400, y: 490, width: 340, height: 340, color: 'purple', z: 0, text: 'Pain Relievers', font_size: 15 },

    { kind: 'text', x: 80, y: 194, width: 300, height: 40, text: 'What we actually offer.', color: 'gray', font_size: 12, z: 3 },
    { kind: 'note', x: 80, y: 240, width: 300, height: 120, text: '', color: 'blue', z: 4 },
    { kind: 'note', x: 80, y: 380, width: 300, height: 120, text: '', color: 'blue', z: 4 },

    // Gain Creators sits top-right so it faces Gains across the gap; Pain
    // Relievers sits bottom-right facing Pains. That pairing is the whole point
    // of the canvas, so the geometry has to preserve it.
    { kind: 'text', x: 420, y: 194, width: 300, height: 40, text: 'How we produce a gain they want.', color: 'gray', font_size: 12, z: 3 },
    { kind: 'note', x: 420, y: 240, width: 300, height: 110, text: '', color: 'green', z: 4 },
    { kind: 'note', x: 420, y: 364, width: 300, height: 110, text: '', color: 'green', z: 4 },

    { kind: 'text', x: 420, y: 534, width: 300, height: 40, text: 'How we remove a specific pain.', color: 'gray', font_size: 12, z: 3 },
    { kind: 'note', x: 420, y: 580, width: 300, height: 110, text: '', color: 'purple', z: 4 },
    { kind: 'note', x: 420, y: 704, width: 300, height: 110, text: '', color: 'purple', z: 4 },

    // ---- the circle --------------------------------------------------------
    // One disc, with the three regions placed where they sit on the real canvas:
    // Jobs on the right, Gains top-left, Pains bottom-left. Every note below is
    // positioned to stay inside radius 340 from the centre (1180, 490) — a note
    // that overflows the disc reads as belonging to nothing.
    { kind: 'ellipse', x: 840, y: 150, width: 680, height: 680, color: 'gray', z: 0 },

    { kind: 'text', x: 1215, y: 258, width: 200, height: 26, text: 'Customer Job(s)', color: 'gray', font_size: 15, z: 3 },
    { kind: 'note', x: 1215, y: 292, width: 240, height: 110, text: '', color: 'yellow', z: 4 },
    { kind: 'note', x: 1215, y: 430, width: 240, height: 110, text: '', color: 'yellow', z: 4 },
    { kind: 'note', x: 1215, y: 568, width: 240, height: 110, text: '', color: 'yellow', z: 4 },

    { kind: 'text', x: 930, y: 268, width: 250, height: 26, text: 'Gains', color: 'gray', font_size: 15, z: 3 },
    { kind: 'note', x: 930, y: 302, width: 250, height: 110, text: '', color: 'green', z: 4 },

    { kind: 'text', x: 930, y: 556, width: 250, height: 26, text: 'Pains', color: 'gray', font_size: 15, z: 3 },
    { kind: 'note', x: 930, y: 590, width: 250, height: 110, text: '', color: 'pink', z: 4 },

    // Fit, drawn in the gap between the two halves. Points right (value ->
    // customer) rather than right-to-left, because board_objects enforces
    // `width >= 0 AND height >= 0` — a negative width is rejected outright, so
    // an arrow's direction has to come from where it starts.
    { kind: 'arrow', x: 752, y: 490, width: 76, height: 0, color: 'gray', z: 5 },

    { kind: 'text', x: 60, y: 862, width: 900, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'Fit: every gain creator answers a real gain, every pain reliever a real pain.' },
  ],
}

// ---------------------------------------------------------------------------
// 4. SWOT — the 2x2, internal over external and helpful over harmful.
// ---------------------------------------------------------------------------
const swot: BoardTemplate = {
  id: 'swot',
  name: 'SWOT',
  description: 'Strengths, Weaknesses, Opportunities, Threats — internal above, external below.',
  background: 'dots',
  preview: [
    { x: 5, y: 12, w: 42, h: 30, color: 'green' },
    { x: 53, y: 12, w: 42, h: 30, color: 'pink' },
    { x: 5, y: 46, w: 42, h: 30, color: 'blue' },
    { x: 53, y: 46, w: 42, h: 30, color: 'orange' },
  ],
  objects: [
    { kind: 'text', x: 60, y: 24, width: 700, height: 34, text: 'SWOT', color: 'gray', font_size: 22, z: 10 },
    { kind: 'text', x: 60, y: 56, width: 1000, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'Top row is inside our control, bottom row is not. Be specific — "communication" is not a strength.' },

    // These sit in their own band above the grid: a frame's title is drawn just
    // ABOVE the frame, so anything level with the top row collides with it.
    { kind: 'text', x: 60, y: 96, width: 200, height: 24, text: 'Helpful', color: 'gray', font_size: 13, z: 10 },
    { kind: 'text', x: 760, y: 96, width: 200, height: 24, text: 'Harmful', color: 'gray', font_size: 13, z: 10 },

    ...zone(60, 148, 620, 340, 'Strengths', { note: 'Internal · what we are genuinely good at.' }),
    { kind: 'note', x: 95, y: 200, width: 170, height: 110, text: '', color: 'green', z: 2 },
    { kind: 'note', x: 280, y: 200, width: 170, height: 110, text: '', color: 'green', z: 2 },

    ...zone(720, 148, 620, 340, 'Weaknesses', { note: 'Internal · where we are honestly short.' }),
    { kind: 'note', x: 755, y: 200, width: 170, height: 110, text: '', color: 'pink', z: 2 },
    { kind: 'note', x: 940, y: 200, width: 170, height: 110, text: '', color: 'pink', z: 2 },

    ...zone(60, 524, 620, 340, 'Opportunities', { note: 'External · openings we could take.' }),
    { kind: 'note', x: 95, y: 576, width: 170, height: 110, text: '', color: 'blue', z: 2 },
    { kind: 'note', x: 280, y: 576, width: 170, height: 110, text: '', color: 'blue', z: 2 },

    ...zone(720, 524, 620, 340, 'Threats', { note: 'External · what could come at us.' }),
    { kind: 'note', x: 755, y: 576, width: 170, height: 110, text: '', color: 'orange', z: 2 },
    { kind: 'note', x: 940, y: 576, width: 170, height: 110, text: '', color: 'orange', z: 2 },

    { kind: 'text', x: 60, y: 890, width: 700, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'Then pair them: which strength takes which opportunity, which weakness exposes which threat.' },
  ],
}

// ---------------------------------------------------------------------------
// 5. Meeple — people as tokens, and the places they can stand.
//
// The board every other template isn't: the objects that matter here are the
// PEOPLE, and the frames exist only to give them somewhere to stand. Six
// meeples start on the bench, one per palette color, because the first thing
// anyone does with this board is drag one out — and an empty board plus a tool
// they have never used is a worse invitation than a piece already in hand.
//
// Unnamed on purpose. A meeple with no text renders its own prompt, which is a
// truer starting point than six placeholder names to delete.
// ---------------------------------------------------------------------------
const meeple: BoardTemplate = {
  id: 'meeple',
  name: 'Meeple',
  description: 'Named person tokens, and lanes to stand them in — who is on what.',
  background: 'dots',
  preview: [
    // A round token is w:8/h:19 rather than a square pair of numbers: the thumb
    // is 52px tall and much wider, so equal percentages draw a flat lozenge.
    { x: 3, y: 10, w: 21, h: 80, outline: true },
    { x: 9, y: 20, w: 8, h: 19, color: 'yellow', round: true },
    { x: 9, y: 52, w: 8, h: 19, color: 'blue', round: true },
    { x: 28, y: 10, w: 21, h: 80, outline: true },
    { x: 34, y: 30, w: 8, h: 19, color: 'green', round: true },
    { x: 52, y: 10, w: 21, h: 80, outline: true },
    { x: 58, y: 56, w: 8, h: 19, color: 'orange', round: true },
    { x: 76, y: 10, w: 21, h: 80, outline: true },
  ],
  objects: [
    { kind: 'text', x: 60, y: 20, width: 800, height: 34, text: 'Who is on what', color: 'gray', font_size: 22, z: 10 },
    { kind: 'text', x: 60, y: 52, width: 1100, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'One meeple per person. Stand them where they actually are today, not where the plan says they are.' },

    // The bench. Its note carries the two things nobody guesses about a meeple:
    // where the name comes from, and where the color comes from.
    ...zone(60, 100, 300, 740, 'Team', {
      note: 'Drag one out. Double-click to name it, and the palette recolors it.',
    }),
    { kind: 'meeple', x: 110, y: 170, width: 76, height: 104, color: 'yellow', z: 4 },
    { kind: 'meeple', x: 230, y: 170, width: 76, height: 104, color: 'blue', z: 4 },
    { kind: 'meeple', x: 110, y: 320, width: 76, height: 104, color: 'green', z: 4 },
    { kind: 'meeple', x: 230, y: 320, width: 76, height: 104, color: 'pink', z: 4 },
    { kind: 'meeple', x: 110, y: 470, width: 76, height: 104, color: 'purple', z: 4 },
    { kind: 'meeple', x: 230, y: 470, width: 76, height: 104, color: 'orange', z: 4 },

    ...zone(420, 100, 220, 740, 'Building', { note: 'Hands on it now.' }),
    ...zone(660, 100, 220, 740, 'Reviewing', { note: 'Reading, testing, pairing.' }),
    ...zone(900, 100, 220, 740, 'Learning', { note: 'Deliberately getting better at something.' }),
    ...zone(1140, 100, 220, 740, 'Blocked', { note: 'Waiting on someone or something else.' }),

    { kind: 'text', x: 420, y: 862, width: 1000, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'A crowded lane next to an empty one is the finding. Two people blocked on the same thing is the other one.' },
  ],
}

// ---------------------------------------------------------------------------
// 6. AI Progress — where AI actually stands for this team, in four lanes.
//
// The lanes are deliberately NOT "good / bad". They are two pairs:
//
//   Leverage  ↔ Friction   what is working now, and what is stopping more
//   Exposure  ↔ Frontier   what this is costing us, and what it might become
//
// Exposure is the lane teams skip, so it gets the sharpest caption. A board
// that only records wins is a board nobody can act on — the useful output is a
// Friction item with an owner and an Exposure item somebody is watching.
//
// Four lanes at 330 wide rather than three at 420: the same ~1500px span the
// other templates use, so a new board still opens showing the whole thing.
// ---------------------------------------------------------------------------
const aiProgress: BoardTemplate = {
  id: 'ai-progress',
  name: 'AI Progress',
  description: 'Four lanes — leverage, friction, exposure, and what is still ahead.',
  background: 'dots',
  preview: [
    { x: 4, y: 14, w: 21, h: 62, color: 'green' },
    { x: 27, y: 14, w: 21, h: 62, color: 'orange' },
    { x: 50, y: 14, w: 21, h: 62, color: 'pink' },
    { x: 73, y: 14, w: 21, h: 62, color: 'purple' },
  ],
  objects: [
    { kind: 'text', x: 60, y: 24, width: 700, height: 34, text: 'AI Progress', color: 'gray', font_size: 22, z: 10 },
    { kind: 'text', x: 60, y: 56, width: 1100, height: 26, font_size: 13, color: 'gray', z: 10,
      text: 'Where AI stands for this team right now. One note per item, with a name on it — "the team" owns nothing.' },

    ...zone(60, 100, 330, 740, 'Leverage', { color: 'green', note: 'Real gains today. Name the task and roughly what it saves.' }),
    { kind: 'note', x: 85, y: 160, width: 140, height: 100, text: '', color: 'green', z: 2 },
    { kind: 'note', x: 235, y: 160, width: 140, height: 100, text: '', color: 'green', z: 2 },

    ...zone(410, 100, 330, 740, 'Friction', { color: 'orange', note: 'What is blocking adoption. Each one needs an owner.' }),
    { kind: 'note', x: 435, y: 160, width: 140, height: 100, text: '', color: 'orange', z: 2 },
    { kind: 'note', x: 585, y: 160, width: 140, height: 100, text: '', color: 'orange', z: 2 },

    ...zone(760, 100, 330, 740, 'Exposure', { color: 'pink', note: 'Risk our own AI use is creating. Review, licensing, data, skills we are losing.' }),
    { kind: 'note', x: 785, y: 160, width: 140, height: 100, text: '', color: 'pink', z: 2 },
    { kind: 'note', x: 935, y: 160, width: 140, height: 100, text: '', color: 'pink', z: 2 },

    ...zone(1110, 100, 330, 740, 'Frontier', { color: 'purple', note: 'Could shift, but has not yet. Worth a timebox, not a plan.' }),
    { kind: 'note', x: 1135, y: 160, width: 140, height: 100, text: '', color: 'purple', z: 2 },
    { kind: 'note', x: 1285, y: 160, width: 140, height: 100, text: '', color: 'purple', z: 2 },

    { kind: 'text', x: 60, y: 790, width: 1380, height: 24, font_size: 12, color: 'gray', z: 5,
      text: 'Finish with one Friction item to clear and one Exposure item to watch. Everything else is a note.' },
  ],
}

/** Start from nothing — the previous behavior, kept as an explicit choice. */
export const BLANK_TEMPLATE: BoardTemplate = {
  id: 'blank',
  name: 'Blank',
  description: 'An empty canvas.',
  background: 'dots',
  preview: [],
  objects: [],
}

export const BOARD_TEMPLATES: BoardTemplate[] = [
  BLANK_TEMPLATE,
  architecture,
  retro,
  valueProp,
  swot,
  meeple,
  aiProgress,
]

/**
 * Nudge every template clear of the tool rail.
 *
 * A board opens at the origin with no pan, and the floating rail sits over the
 * left edge of the viewport — so anything laid out below roughly x=90 in board
 * space is behind it on arrival, which made the first heading of every template
 * render half-hidden. Applied here rather than baked into each coordinate so
 * the layouts above stay readable as relative geometry.
 */
const LAYOUT_ORIGIN_X = 120

/** Rows ready for a bulk insert into board_objects. */
export function templateRows(template: BoardTemplate, boardId: string, teamId: string) {
  return template.objects.map((o) => ({
    board_id: boardId,
    team_id: teamId,
    kind: o.kind,
    x: o.x + LAYOUT_ORIGIN_X,
    y: o.y,
    width: o.width,
    height: o.height,
    text: o.text ?? null,
    color: o.color ?? 'yellow',
    z: o.z ?? 0,
    font_size: o.font_size ?? 16,
  }))
}
